// Per-product packaging, storage and commercial details, keyed by slug.
// Falls back to sensible defaults when a product does not override.

const DEFAULT_PACKAGING = [
  { title: "Retail Packs", text: "Branded retail SKUs · private-label option." },
  { title: "Bulk Packs", text: "Wholesale & HoReCa formats." },
  { title: "Container", text: "Standard / reefer container as per commodity." },
];

const DEFAULT_STORAGE = [
  ["Storage Temp", "As per commodity"],
  ["Relative Humidity", "As per commodity"],
  ["Shelf Life", "As per commodity"],
  ["Transit Mode", "Standard / reefer container"],
];

const DEFAULT_COMMERCIAL = [
  ["Minimum Order", "1 × 20' / 40' container"],
  ["Lead Time", "7 – 14 days from PO"],
  ["Incoterms", "FOB · CFR · CIF"],
  ["Payment", "30% advance · 70% against B/L"],
  ["Port of Loading", "Nhava Sheva · Mundra"],
  ["Sample Policy", "Free for confirmed orders"],
];

export const productDetails = {
  // ─── VEGETABLES ─────────────────────────────────────────
  onion: {
    packaging: [
      { title: "Retail Packs", text: "1 / 2 / 3 / 5 kg mesh bags · branded retail SKUs · private label." },
      { title: "Bulk Packs", text: "10 / 20 / 25 / 45 / 50 kg PP mesh sacks · jute bags on request." },
      { title: "Reefer Container", text: "Ventilated 40' container · ~26 MT FCL · pre-conditioned." },
    ],
    storage: [
      ["Storage Temp", "0 to +2 °C"],
      ["Relative Humidity", "65 – 70%"],
      ["Shelf Life", "4 – 6 months (cured)"],
      ["Transit Mode", "Ventilated reefer container"],
    ],
  },

  potato: {
    packaging: [
      { title: "Retail Packs", text: "2 / 3 / 5 kg mesh nets · washed & graded retail packs." },
      { title: "Bulk Packs", text: "10 / 25 / 50 kg jute & PP woven bags for wholesale." },
      { title: "Reefer Container", text: "40' reefer at +4 to +8 °C · ~26 MT FCL." },
    ],
    storage: [
      ["Storage Temp", "+4 to +6 °C (table) · +8 to +10 °C (processing)"],
      ["Relative Humidity", "90 – 95%"],
      ["Shelf Life", "4 – 8 months (controlled storage)"],
      ["Transit Mode", "Reefer container"],
    ],
  },

  "green-chilli": {
    packaging: [
      { title: "Retail Packs", text: "3 / 5 kg ventilated cartons · 200 g – 1 kg vacuum / MA pillow packs." },
      { title: "Bulk Packs", text: "10 kg ventilated export cartons for wholesale & HoReCa." },
      { title: "Reefer / Air", text: "40' reefer at +8 °C · cold air freight for short-shelf-life lots." },
    ],
    storage: [
      ["Storage Temp", "+7 to +10 °C"],
      ["Relative Humidity", "90 – 95%"],
      ["Shelf Life", "10 – 14 days fresh · up to 21 days (MA / vacuum pack)"],
      ["Transit Mode", "Reefer container · cold air freight"],
    ],
  },

  okra: {
    packaging: [
      { title: "Retail Packs", text: "250 g – 500 g punnets · supermarket-ready trays." },
      { title: "Bulk Packs", text: "3 / 5 kg ventilated export cartons." },
      { title: "Air / Reefer", text: "Cold air freight preferred · +10 °C reefer for sea routes to GCC." },
    ],
    storage: [
      ["Storage Temp", "+7 to +10 °C"],
      ["Relative Humidity", "90 – 95%"],
      ["Shelf Life", "7 – 10 days from harvest"],
      ["Transit Mode", "Cold air freight · reefer container"],
    ],
    commercial: [
      ["Minimum Order", "1 pallet (air) · 1 × 40' reefer (sea)"],
      ["Lead Time", "3 – 7 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L or AWB"],
      ["Port of Loading", "Mumbai · Nhava Sheva · BLR / BOM (air)"],
      ["Sample Policy", "Free for confirmed orders"],
    ],
  },

  "sweet-corn": {
    packaging: [
      { title: "Husk-on Cartons", text: "10 kg ventilated cartons · 8 / 12-cob retail trays." },
      { title: "IQF Bulk Bags", text: "1 / 5 / 10 kg IQF kernel poly bags for foodservice." },
      { title: "Reefer", text: "+4 °C for fresh cobs · -18 °C frozen reefer for IQF." },
    ],
    storage: [
      ["Storage Temp", "0 to +4 °C (fresh) · -18 °C (IQF)"],
      ["Relative Humidity", "90 – 95% (fresh)"],
      ["Shelf Life", "5 – 8 days fresh · 18 – 24 months IQF"],
      ["Transit Mode", "Reefer container · frozen reefer for IQF"],
    ],
  },

  ginger: {
    packaging: [
      { title: "Retail Packs", text: "500 g – 1 kg cartons & punnets · branded private label." },
      { title: "Bulk Packs", text: "10 / 20 kg jute bags & corrugated cartons for wholesale." },
      { title: "Reefer", text: "Pre-conditioned 40' reefer at +12 to +13 °C." },
    ],
    storage: [
      ["Storage Temp", "+12 to +13 °C"],
      ["Relative Humidity", "85 – 90%"],
      ["Shelf Life", "3 – 4 weeks (washed) · up to 6 months (cured storage)"],
      ["Transit Mode", "Reefer container"],
    ],
  },

  // ─── FRUITS ─────────────────────────────────────────────
  pomegranate: {
    packaging: [
      { title: "Retail Cartons", text: "3.5 / 4.5 kg telescopic cartons with foam-mesh sleeves." },
      { title: "Punnet Packs", text: "500 g – 1 kg punnets in master cartons · supermarket-ready." },
      { title: "Reefer", text: "+5 °C reefer · 90 – 95% RH · SO₂ pads available on request." },
    ],
    storage: [
      ["Storage Temp", "+5 °C"],
      ["Relative Humidity", "90 – 95%"],
      ["Shelf Life", "4 – 6 weeks from pack date"],
      ["Transit Mode", "Reefer container"],
    ],
  },

  banana: {
    packaging: [
      { title: "Telescopic Cartons", text: "13 / 13.5 / 18 kg polylined telescopic cartons." },
      { title: "Cluster Packs", text: "Retail clusters · custom hand-counts for SKUs." },
      { title: "Reefer", text: "+13.3 °C ethylene-controlled reefer with CO₂ scrubbers." },
    ],
    storage: [
      ["Storage Temp", "+13.3 °C (green) · +14 to +16 °C (ripening)"],
      ["Relative Humidity", "85 – 95%"],
      ["Shelf Life", "21 – 28 days from pack (green)"],
      ["Transit Mode", "Ethylene-controlled reefer container"],
    ],
    commercial: [
      ["Minimum Order", "1 × 40' reefer (~ 1080 cartons / 19 MT)"],
      ["Lead Time", "7 – 10 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L"],
      ["Port of Loading", "Mundra · Nhava Sheva"],
      ["Sample Policy", "Photo / video samples for confirmed orders"],
    ],
  },

  mango: {
    packaging: [
      { title: "Retail Cartons", text: "2 / 4 / 5 kg single-layer trays with paper cushion." },
      { title: "Bulk Cartons", text: "5 / 10 kg cartons for wholesale & processing." },
      { title: "Reefer / Air", text: "+12 °C reefer for sea · air freight for premium markets." },
    ],
    storage: [
      ["Storage Temp", "+12 to +13 °C"],
      ["Relative Humidity", "85 – 90%"],
      ["Shelf Life", "14 – 21 days (HWT-treated)"],
      ["Transit Mode", "Reefer container · cold air freight"],
    ],
    commercial: [
      ["Minimum Order", "1 pallet (air) · 1 × 40' reefer (sea)"],
      ["Lead Time", "3 – 7 days in-season"],
      ["Incoterms", "FOB · CFR · CIF · CIP (air)"],
      ["Payment", "30% advance · 70% against B/L or AWB"],
      ["Port of Loading", "Mumbai · Nhava Sheva · BOM / HYD (air)"],
      ["Sample Policy", "Free samples for confirmed orders"],
    ],
  },

  "table-grapes": {
    packaging: [
      { title: "Punnet Packs", text: "500 g punnets in 4.5 / 5 kg master cartons (retail-ready)." },
      { title: "Open Cartons", text: "4.5 / 5 / 8 kg open cartons with liner & SO₂ generator pad." },
      { title: "Reefer", text: "0 to -1 °C reefer with dual-action SO₂ pads." },
    ],
    storage: [
      ["Storage Temp", "0 to -1 °C"],
      ["Relative Humidity", "90 – 95%"],
      ["Shelf Life", "30 – 45 days (with SO₂ pads)"],
      ["Transit Mode", "Reefer container with SO₂ generator pads"],
    ],
  },

  coconut: {
    packaging: [
      { title: "Mature Coconut", text: "PP mesh / jute bags · 25 kg loose · trimmed cartons for retail." },
      { title: "Tender Coconut", text: "Wax-coated, individually shrink-wrapped · 8 / 10 / 12-pc cartons." },
      { title: "Reefer / Ambient", text: "Ambient FCL for mature · +4 °C reefer for tender." },
    ],
    storage: [
      ["Storage Temp", "Ambient (mature) · 0 to +4 °C (tender)"],
      ["Relative Humidity", "75 – 85%"],
      ["Shelf Life", "3 – 6 months (mature) · 4 – 6 weeks (tender, waxed)"],
      ["Transit Mode", "Standard / reefer container"],
    ],
    commercial: [
      ["Minimum Order", "1 × 20' / 40' container"],
      ["Lead Time", "7 – 14 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L"],
      ["Port of Loading", "Tuticorin · Cochin · Nhava Sheva"],
      ["Sample Policy", "Free for confirmed orders"],
    ],
  },

  // ─── SPICES ─────────────────────────────────────────────
  turmeric: {
    packaging: [
      { title: "Bulk Bags", text: "25 / 50 kg PP woven or jute bags · liner option." },
      { title: "Retail Packs", text: "100 g – 1 kg branded pouches · private-label option." },
      { title: "Container", text: "Standard 20' / 40' container · ~22 – 25 MT FCL." },
    ],
    storage: [
      ["Storage Temp", "Ambient · cool, dry warehouse"],
      ["Relative Humidity", "Below 65%"],
      ["Shelf Life", "18 – 24 months (whole) · 12 months (powder)"],
      ["Transit Mode", "Standard dry container"],
    ],
    commercial: [
      ["Minimum Order", "5 MT (LCL) · 1 × 20' / 40' container (FCL)"],
      ["Lead Time", "5 – 10 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L (or LC at sight)"],
      ["Port of Loading", "Nhava Sheva · Cochin · Tuticorin"],
      ["Sample Policy", "Free 100 g samples · courier at buyer's cost"],
    ],
  },

  cumin: {
    packaging: [
      { title: "Bulk Bags", text: "25 / 50 kg PP woven & jute bags · liner option." },
      { title: "Retail Packs", text: "50 g – 1 kg pouches · private-label retail SKUs." },
      { title: "Container", text: "Standard 20' / 40' container · ~22 – 24 MT FCL." },
    ],
    storage: [
      ["Storage Temp", "Ambient · cool, dry warehouse"],
      ["Relative Humidity", "Below 65%"],
      ["Shelf Life", "18 – 24 months"],
      ["Transit Mode", "Standard dry container"],
    ],
    commercial: [
      ["Minimum Order", "5 MT (LCL) · 1 × 20' / 40' container (FCL)"],
      ["Lead Time", "5 – 10 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L (or LC at sight)"],
      ["Port of Loading", "Mundra · Nhava Sheva"],
      ["Sample Policy", "Free 100 g samples · courier at buyer's cost"],
    ],
  },

  coriander: {
    packaging: [
      { title: "Bulk Bags", text: "25 / 50 kg PP woven bags · liner option." },
      { title: "Retail Packs", text: "100 g – 1 kg pouches · private-label option." },
      { title: "Container", text: "Standard 20' / 40' container · ~22 MT FCL." },
    ],
    storage: [
      ["Storage Temp", "Ambient · cool, dry warehouse"],
      ["Relative Humidity", "Below 65%"],
      ["Shelf Life", "18 – 24 months (whole seed)"],
      ["Transit Mode", "Standard dry container"],
    ],
    commercial: [
      ["Minimum Order", "5 MT (LCL) · 1 × 20' / 40' container (FCL)"],
      ["Lead Time", "5 – 10 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L"],
      ["Port of Loading", "Mundra · Nhava Sheva · Kandla"],
      ["Sample Policy", "Free 100 g samples · courier at buyer's cost"],
    ],
  },

  "black-pepper": {
    packaging: [
      { title: "Bulk Bags", text: "25 / 50 kg PP woven & jute bags · liner option." },
      { title: "Vacuum Packs", text: "5 / 10 kg vacuum bags for premium retail & flavour retention." },
      { title: "Container", text: "Standard 20' / 40' container · ~22 MT FCL." },
    ],
    storage: [
      ["Storage Temp", "Ambient · cool, dry warehouse"],
      ["Relative Humidity", "Below 65%"],
      ["Shelf Life", "24 months (whole) · 12 months (ground)"],
      ["Transit Mode", "Standard dry container"],
    ],
    commercial: [
      ["Minimum Order", "1 MT (LCL) · 1 × 20' / 40' container (FCL)"],
      ["Lead Time", "5 – 10 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L (or LC at sight)"],
      ["Port of Loading", "Cochin · Nhava Sheva"],
      ["Sample Policy", "Free 100 g samples · courier at buyer's cost"],
    ],
  },

  cardamom: {
    packaging: [
      { title: "Premium Cartons", text: "5 / 10 kg cartons with food-grade liner." },
      { title: "Vacuum Packs", text: "1 / 5 kg vacuum bags to preserve volatile oil & colour." },
      { title: "Reefer (premium)", text: "+5 to +10 °C reefer recommended for top-grade colour retention." },
    ],
    storage: [
      ["Storage Temp", "Ambient · ideally +5 to +10 °C for premium grades"],
      ["Relative Humidity", "Below 60%"],
      ["Shelf Life", "12 months (vacuum, cool storage)"],
      ["Transit Mode", "Standard / reefer container for premium grades"],
    ],
    commercial: [
      ["Minimum Order", "100 kg (LCL) · 1 × 20' container (FCL)"],
      ["Lead Time", "5 – 10 days · subject to daily auction availability"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "50% advance · 50% against B/L (or LC at sight)"],
      ["Port of Loading", "Cochin"],
      ["Sample Policy", "Paid samples · adjustable against confirmed order"],
    ],
  },

  cinnamon: {
    packaging: [
      { title: "Bulk Cartons", text: "10 / 25 kg corrugated cartons · liner option." },
      { title: "Retail Packs", text: "50 g – 500 g pouches · whole quills, cuts or ground." },
      { title: "Container", text: "Standard 20' / 40' container · ambient." },
    ],
    storage: [
      ["Storage Temp", "Ambient · cool, dry warehouse"],
      ["Relative Humidity", "Below 65%"],
      ["Shelf Life", "24 months (quills) · 12 months (powder)"],
      ["Transit Mode", "Standard dry container"],
    ],
    commercial: [
      ["Minimum Order", "1 MT (LCL) · 1 × 20' container (FCL)"],
      ["Lead Time", "7 – 14 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L"],
      ["Port of Loading", "Colombo (Ceylon) · Tuticorin · Cochin (Cassia)"],
      ["Sample Policy", "Free 200 g samples · courier at buyer's cost"],
    ],
  },

  // ─── PROCESSED ──────────────────────────────────────────
  "dehydrated-vegetables": {
    packaging: [
      { title: "Industrial Cartons", text: "10 / 20 / 25 kg foil-lined cartons with inner poly liner." },
      { title: "Retail Packs", text: "100 g – 1 kg foil pouches · private-label option." },
      { title: "Container", text: "Standard 20' / 40' container · low-humidity stuffing." },
    ],
    storage: [
      ["Storage Temp", "Ambient · ≤ 25 °C"],
      ["Relative Humidity", "Below 60%"],
      ["Shelf Life", "18 – 24 months (sealed, dry)"],
      ["Transit Mode", "Standard dry container"],
    ],
    commercial: [
      ["Minimum Order", "1 MT (LCL) · 1 × 20' / 40' container (FCL)"],
      ["Lead Time", "10 – 21 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L (or LC at sight)"],
      ["Port of Loading", "Mundra · Nhava Sheva"],
      ["Sample Policy", "Free 200 g samples · courier at buyer's cost"],
    ],
  },

  "fruit-pulps": {
    packaging: [
      { title: "Aseptic Drums", text: "215 kg aseptic bag-in-steel-drum for industrial use." },
      { title: "Retail Tins / Pouches", text: "450 g / 850 g tins · 1 kg retail pouches." },
      { title: "Container", text: "Ambient FCL (aseptic) · -18 °C reefer (frozen)." },
    ],
    storage: [
      ["Storage Temp", "Ambient (aseptic) · -18 °C (frozen)"],
      ["Relative Humidity", "Not critical (sealed)"],
      ["Shelf Life", "18 months aseptic · 24 months frozen"],
      ["Transit Mode", "Standard / -18 °C reefer container"],
    ],
    commercial: [
      ["Minimum Order", "1 × 20' container (~ 80 drums)"],
      ["Lead Time", "10 – 21 days · season-bound for mango"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L (or LC at sight)"],
      ["Port of Loading", "Nhava Sheva · Mundra"],
      ["Sample Policy", "Free 1 kg samples · courier at buyer's cost"],
    ],
  },

  raisins: {
    packaging: [
      { title: "Bulk Cartons", text: "10 / 12.5 / 14 kg corrugated cartons with food-grade poly inner liner." },
      { title: "Retail Pouches", text: "200 g / 250 g / 500 g / 1 kg stand-up pouches · private-label option." },
      { title: "Container", text: "Standard 20' / 40' container · ~22 – 25 MT FCL · cool, dry stuffing." },
    ],
    storage: [
      ["Storage Temp", "+5 to +15 °C ideal · ambient ≤ +20 °C acceptable (sugaring above)"],
      ["Relative Humidity", "60 – 65% max"],
      ["Shelf Life", "12 – 18 months sealed (longer in cool storage)"],
      ["Transit Mode", "Standard dry container · reefer recommended for hot routes / summer transit"],
    ],
    commercial: [
      ["Minimum Order", "1 MT (LCL) · 1 × 20' / 40' container (FCL)"],
      ["Lead Time", "7 – 14 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L (or LC at sight)"],
      ["Port of Loading", "Nhava Sheva · Mundra (origin: Sangli, Maharashtra)"],
      ["Sample Policy", "Free 250 g samples · courier at buyer's cost"],
    ],
  },

  "iqf-corn": {
    packaging: [
      { title: "IQF Cobs", text: "Half-cobs in 1 / 5 / 10 kg poly bags · master cartons." },
      { title: "IQF Kernels", text: "Free-flowing kernels in 1 / 5 / 10 kg poly bags." },
      { title: "Frozen Reefer", text: "-18 °C reefer container · ~22 MT FCL." },
    ],
    storage: [
      ["Storage Temp", "-18 °C or lower"],
      ["Relative Humidity", "Not critical (frozen)"],
      ["Shelf Life", "18 – 24 months at -18 °C"],
      ["Transit Mode", "Frozen reefer container"],
    ],
    commercial: [
      ["Minimum Order", "1 × 20' / 40' frozen reefer"],
      ["Lead Time", "10 – 21 days from PO"],
      ["Incoterms", "FOB · CFR · CIF"],
      ["Payment", "30% advance · 70% against B/L (or LC at sight)"],
      ["Port of Loading", "Nhava Sheva · Mundra"],
      ["Sample Policy", "Free 1 kg samples · cold-chain courier at buyer's cost"],
    ],
  },
};

export const getProductDetails = (slug) => {
  const d = productDetails[slug] || {};
  return {
    packaging: d.packaging || DEFAULT_PACKAGING,
    storage: d.storage || DEFAULT_STORAGE,
    commercial: d.commercial || DEFAULT_COMMERCIAL,
  };
};
